import type { PublicKey } from "@solana/web3.js";
import type { MercurialFarmPosition } from "@stepfinance/farms/src/lib/positions/mercurial/types";
import type { PendingReward } from "@stepfinance/liquidity";
import { gql, GraphQLClient } from "graphql-request";
import { useEffect, useState } from "react";
import useSWR from "swr";

import { useAccountsContext } from "../../contexts/accounts";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLiquidity } from "../../contexts/liquidity";
import { useMarkets } from "../../contexts/market";
import { priceInUSDWrapper } from "../../utils/markets/getPriceInUSD";
import { parseMercurialRewards } from "../../utils/mercurial/rewards";
import { getTotalLiquidityInUSD } from "../../utils/pools/utils";
import { getTokenByMint, getTokenBySymbol } from "../../utils/tokenInfo";

const graphqlClient = new GraphQLClient("https://api.mercurial.finance/graphql");

type MercurialRewardsData = {
  rewardsForWalletWithPaging: {
    nodes: MercurialReward[];
  };
};
export type MercurialReward = {
  id: string;
  name: string;
  pool: string;
  transactionAddress: string | null;
  transactionStatus: string | null;
};
export const useMercurialFarms = () => {
  const { connected, publicKey } = useEnrichedWallet();
  const { mercurialPools } = useLiquidity();
  const { marketsInitialised } = useMarkets();
  const { userTokenAccounts } = useAccountsContext();
  const [mercurialFarms, setMercurialFarms] = useState<MercurialFarmPosition[]>([]);

  const { priceInUSD } = useMarkets();

  const pendingRewardsQuery = gql`
    query ($address: String!) {
      rewardsForWalletWithPaging(address: $address, status: "unclaimed") {
        nodes {
          id
          name
          pool
          transactionAddress
          transactionStatus
        }
      }
    }
  `;
  const fetcher = (query: string, publicKey: PublicKey | null | undefined) =>
    graphqlClient.request(query, {
      address: publicKey?.toBase58() || "",
    });

  const { data: mercurialRewardsData } = useSWR<MercurialRewardsData>([pendingRewardsQuery, publicKey], fetcher, {
    errorRetryCount: 1,
  });

  useEffect(() => setMercurialFarms([]), [connected]);

  useEffect(() => {
    if (
      !publicKey ||
      !marketsInitialised ||
      userTokenAccounts.length === 0 ||
      Object.keys(mercurialPools).length === 0
    ) {
      return;
    }

    const mercurialPoolMintAddresses = new Set(Object.keys(mercurialPools));
    const userStakedPools = userTokenAccounts.filter((account) => {
      return mercurialPoolMintAddresses.has(account.info.mint);
    });

    if (userStakedPools.length === 0) {
      return;
    }

    const rewardsData = parseMercurialRewards(
      mercurialPools,
      mercurialRewardsData?.rewardsForWalletWithPaging?.nodes
        ? mercurialRewardsData.rewardsForWalletWithPaging.nodes
        : [],
    );

    const farms: MercurialFarmPosition[] = [];
    for (const account of userStakedPools) {
      const pool = mercurialPools[account.info.mint];
      const userRatio = pool.supply ? account.tokenAmount.uiAmount / pool.supply : 0;
      const lpMints = pool.mints.map((mint) => getTokenByMint(mint));
      const liquidity = pool.liquidity.map((l, i) => {
        return { token: lpMints[i], amount: l * userRatio };
      });

      const totalValue = getTotalLiquidityInUSD(pool.liquidityInUSD) * userRatio;

      const apr = pool.apy ? pool.apy * 100 : 0;

      const poolRewards = rewardsData[pool.accountAddress];
      const rewards: PendingReward[] = [];
      if (poolRewards) {
        const rewardKeys = Object.keys(poolRewards);
        const rewardAmountA = poolRewards[rewardKeys[0]].total;
        const rewardAInfo = getTokenBySymbol(rewardKeys[0]);
        if (rewardAInfo) {
          rewards.push({
            rewardToken: rewardAInfo,
            rewardBalance: rewardAmountA,
            rewardInUSD: rewardAmountA * priceInUSDWrapper(priceInUSD(rewardAInfo?.address) ?? 0),
          });
        }
        if (rewardKeys.length > 1) {
          const rewardBInfo = getTokenBySymbol(rewardKeys[1]);
          const rewardAmountB = poolRewards[rewardKeys[1]].total;
          if (rewardBInfo) {
            rewards.push({
              rewardToken: rewardBInfo,
              rewardBalance: rewardAmountB,
              rewardInUSD: rewardAmountB * priceInUSDWrapper(priceInUSD(rewardBInfo?.address) ?? 0),
            });
          }
        }
      }

      const farm: MercurialFarmPosition = {
        key: pool.accountAddress,
        kind: "Mercurial",
        lpName: pool.mintNames.join("/"),
        lpMintAddress: pool.mintAddress,
        link: pool.poolLink || "",
        description: "",
        valueInUSD: totalValue,
        liquidity,
        rewards,
        apr,
      };

      farms.push(farm);
    }

    setMercurialFarms(farms);
  }, [
    marketsInitialised,
    mercurialPools,
    mercurialRewardsData?.rewardsForWalletWithPaging?.nodes,
    priceInUSD,
    publicKey,
    userTokenAccounts,
  ]);
  return mercurialFarms;
};
